import { redirect } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import type { UserRole } from "@/lib/auth-client";
import { authMiddleware } from "@/middleware/auth";

/**
 * Valid user roles in the system
 */
const VALID_ROLES: readonly UserRole[] = ["admin", "designer", "client"];

/**
 * Type guard to validate that a value is a valid UserRole
 */
function isValidRole(role: unknown): role is UserRole {
	return typeof role === "string" && VALID_ROLES.includes(role as UserRole);
}

/**
 * Server function to require admin role for protected routes.
 *
 * Use in route `beforeLoad` to enforce role-based access at the server level,
 * preventing client-side flash and providing proper SSR protection.
 *
 * @example
 * ```ts
 * export const Route = createFileRoute("/_dashboard/admin/")({
 *   beforeLoad: async () => {
 *     await requireAdmin();
 *   },
 * });
 * ```
 */
export const requireAdmin = createServerFn({ method: "GET" })
	.middleware([authMiddleware])
	.handler(async ({ context }) => {
		const session = context.session;

		if (!session?.data?.user) {
			throw redirect({ to: "/login" });
		}

		const role = session.data.user.role;
		if (!isValidRole(role) || role !== "admin") {
			throw redirect({ to: "/" });
		}

		return { session: session.data };
	});

/**
 * Server function to require designer or admin role for protected routes.
 */
export const requireDesigner = createServerFn({ method: "GET" })
	.middleware([authMiddleware])
	.handler(async ({ context }) => {
		const session = context.session;

		if (!session?.data?.user) {
			throw redirect({ to: "/login" });
		}

		const role = session.data.user.role;
		if (!isValidRole(role) || (role !== "admin" && role !== "designer")) {
			throw redirect({ to: "/" });
		}

		return { session: session.data };
	});

/**
 * Server function to require client role for protected routes.
 */
export const requireClient = createServerFn({ method: "GET" })
	.middleware([authMiddleware])
	.handler(async ({ context }) => {
		const session = context.session;

		if (!session?.data?.user) {
			throw redirect({ to: "/login" });
		}

		const role = session.data.user.role;
		if (!isValidRole(role) || role !== "client") {
			throw redirect({ to: "/" });
		}

		return { session: session.data };
	});
